import { Button, Card, EmptyState, Icon, LinkButton, Markdown, Skeleton } from "@iec-id/ui";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AuthBrandBar } from "@/components/signin/auth-card";
import { useLegalT } from "./i18n";
import { LegalDocumentNotFound, usePublicLegalDocument } from "./public-legal";

export interface PublicDocumentProps {
	documentKey: string;
	/** Локаль из адреса. Пусто — берём язык интерфейса. */
	locale?: string | undefined;
}

/**
 * Публичная читалка юридического документа — `sso.iec.exchange/legal/<key>`.
 *
 * Живёт на экранах входа, а не в витрине, ровно по одной причине: витрина
 * целиком стоит за сессией (её оболочка тянет профиль, Баллы и сокет), а этот
 * адрес обязан открываться у гостя — на него ведут баннер cookie, футер и
 * письма.
 *
 * Тексты только на английском (Д10). Подпись «нет на выбранном языке»
 * не показываем: человек и так читает английский юридический текст.
 */
export function PublicDocument({ documentKey, locale }: PublicDocumentProps) {
	const t = useLegalT();
	const { i18n } = useTranslation();
	const requested = locale ?? i18n.language;
	const query = usePublicLegalDocument(documentKey, requested);

	const notFound = query.error instanceof LegalDocumentNotFound;
	const document = query.data;

	return (
		<Card padding="auth" gap="row" className="w-full max-w-[760px]">
			{/* Документ открывают по прямой ссылке — знак отвечает на «чей это
			    документ», переключатель языка даёт вторую локаль текста. */}
			<AuthBrandBar />
			{query.isPending ? (
				<div className="flex flex-col gap-3" role="status" aria-label={t("reader.loading")}>
					<Skeleton height={28} radius="control" />
					<Skeleton height={16} radius="chip" />
					<Skeleton height={220} radius="control" />
				</div>
			) : null}

			{notFound ? (
				<EmptyState
					icon={<Icon name="CircleAlert" size={32} />}
					title={t("reader.notFoundTitle")}
					description={t("reader.notFoundDescription")}
					action={
						<Link to="/legal">
							<Button variant="second">{t("reader.back")}</Button>
						</Link>
					}
				/>
			) : null}

			{query.isError && !notFound ? (
				<EmptyState
					icon={<Icon name="TriangleAlert" size={32} />}
					title={t("reader.errorTitle")}
					action={
						<Button variant="second" onClick={() => void query.refetch()}>
							{t("reader.retry")}
						</Button>
					}
				/>
			) : null}

			{document ? (
				<>
					<header className="flex flex-col gap-1.5">
						<h1 className="t-section">{document.title}</h1>
						<span className="t-meta text-fg-muted">
							{t("reader.version", {
								version: document.version,
								date: new Date(document.effectiveFrom),
							})}
						</span>
					</header>

					<Markdown source={document.bodyMarkdown} className="t-body text-fg-secondary" />

					{document.locales.length > 1 ? (
						<div className="flex flex-wrap items-center gap-3">
							<span className="t-meta text-fg-muted">{t("reader.otherLocales")}</span>
							{document.locales.map((version) => (
								<LinkButton
									key={version.locale}
									href={`/legal/${document.key}?lang=${version.locale}`}
								>
									{version.locale.toUpperCase()}
								</LinkButton>
							))}
						</div>
					) : null}

					<footer className="flex flex-wrap gap-4">
						<Link to="/legal">
							<LinkButton>{t("reader.back")}</LinkButton>
						</Link>
						<Link to="/">
							<LinkButton>{t("reader.signin")}</LinkButton>
						</Link>
					</footer>
				</>
			) : null}
		</Card>
	);
}
